'use client';
import { useGetUploadBucket } from '@unifyapps/network/generated/file-rest-api/file-rest-api';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';

type PlatformInterfaceFlags = Partial<InterfaceEntity['properties']['flags']>;
type FlagsContextType = PlatformInterfaceFlags & {
  isPublicUploadAllowed: boolean;
};

const FlagsContext = createContext<FlagsContextType | null>(null);

export function useFlags() {
  const context = useContext(FlagsContext);
  if (context === null) {
    throw new Error('FlagsContext must be used within a FlagsProvider');
  }
  return context;
}

export default function FlagsProvider({
  isUploadAllowed,
  flags = {},
  children,
}: {
  flags?: PlatformInterfaceFlags;
  isUploadAllowed: boolean;
  children: ReactNode;
}) {
  const { showBreadCrumbInIframe = false, enableAutomationTemplates = true } = flags;
  const { data } = useGetUploadBucket(
    { publicBucket: true },
    { query: { staleTime: Infinity, enabled: isUploadAllowed, retry: false } },
  );

  const isPublicUploadAllowed = isUploadAllowed && Boolean(data?.bucket);
  const context = useMemo(
    () => ({
      isPublicUploadAllowed,
      showBreadCrumbInIframe,
      enableAutomationTemplates,
    }),
    [isPublicUploadAllowed, showBreadCrumbInIframe, enableAutomationTemplates],
  );

  return <FlagsContext.Provider value={context}>{children}</FlagsContext.Provider>;
}
